import React, { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { fetchMyMedia, type MyMedia } from '@/services/siteApi';

// "Your photos and reels" on /my-site: the headshot, photos, reels and résumé from
// the actor's casting profile as links, so they can hand them to whatever builds
// their site. Each link is permanent (casting re-signs it on every visit).

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const quiet: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 36, padding: '6px 12px', borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', cursor: 'pointer', flexShrink: 0 };
const REEL_LABEL: Record<string, string> = { demo_reel: 'Reel', self_tape: 'Self-tape', commercial: 'Commercial', theatrical: 'Theatrical' };

type Row = { label: string; url: string; thumb?: boolean };
export function mediaRows(m: MyMedia): Row[] {
  const rows: Row[] = [];
  if (m.headshot) rows.push({ label: 'Headshot', url: m.headshot, thumb: true });
  m.photos.forEach((u, i) => rows.push({ label: `Photo ${i + 1}`, url: u, thumb: true }));
  m.reels.forEach((r) => rows.push({ label: [REEL_LABEL[r.type || ''] || 'Video', r.title].filter(Boolean).join(': '), url: r.url }));
  if (m.resume) rows.push({ label: 'Résumé', url: m.resume });
  return rows;
}
export const mediaText = (rows: Row[]) => rows.map((r) => `${r.label}: ${r.url}`).join('\n');

const CopyBtn: React.FC<{ text: string; label?: string; aria: string }> = ({ text, label = 'Copy', aria }) => {
  const [done, setDone] = useState(false);
  return (
    <button type="button" aria-label={aria} style={quiet} onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1800); }).catch(() => {}); }}>
      {done ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}{done ? 'Copied' : label}
    </button>
  );
};

const SiteMediaLinks: React.FC<{ onLoaded?: (m: MyMedia) => void }> = ({ onLoaded }) => {
  const [media, setMedia] = useState<MyMedia | null>(null);
  useEffect(() => { fetchMyMedia().then((m) => { setMedia(m); onLoaded?.(m); }).catch(() => setMedia(null)); }, []);
  if (!media) return null;
  const rows = mediaRows(media);
  if (!rows.length) return null;
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <h2 style={{ margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK }}>Your photos and reels</h2>
        <CopyBtn text={mediaText(rows)} label="Copy all" aria="Copy every link" />
      </div>
      <p style={small}>From your Hey Joe profile, copy these links into ChatGPT, Claude, or share them with your designer. They’ll keep working as you build your site.</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((r) => (
          <li key={r.url} className="flex items-center" style={{ gap: 12, padding: '10px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
            {r.thumb
              ? <img src={r.url} alt="" loading="lazy" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, background: 'var(--hj-surface-2)', flexShrink: 0 }} />
              : <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 8, background: 'var(--hj-surface-2)', flexShrink: 0 }} />}
            <div className="flex flex-col" style={{ minWidth: 0, flex: 1 }}>
              <a href={r.url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, fontSize: 15, color: INK, textDecoration: 'none', wordBreak: 'break-word' }}>{r.label}</a>
              <span style={{ ...small, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.url}</span>
            </div>
            <CopyBtn text={r.url} aria={`Copy the ${r.label} link`} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SiteMediaLinks;
